import React, { useState, useEffect } from 'react';
import ConfirmationModal from '../modals/ConfirmationModal';
import { OrderItem } from '../types';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { formatCurrency } from '../util/formatCurrency';
import { toast } from 'react-toastify';

interface OrderItemsTableProps {
  orderId: number;
  orderItems: OrderItem[];
  autoExtraction?: boolean;
  vatIsCustom?: boolean;
  onSaArrival?: () => void;
  onEswatiniArrival?: () => void;
  onViewCart?: () => void;
  cartStoreName?: string;
  onRetryCart?: () => void;
  cartRetryState?: 'idle' | 'sending' | 'requested';
}

const OrderItemsTable: React.FC<OrderItemsTableProps> = ({ orderId, orderItems: initialOrderItems, autoExtraction, vatIsCustom, onSaArrival, onEswatiniArrival, onViewCart, cartStoreName, onRetryCart, cartRetryState = 'idle' }) => {
  // The per-item "Charge VAT?" column only applies when the order's VAT is auto-calculated.
  const showChargeVatColumn = !vatIsCustom;
  const [orderItems, setOrderItems] = useState<OrderItem[]>(initialOrderItems);
  const [showArrivalModal, setShowArrivalModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [pendingItemId, setPendingItemId] = useState<number | null>(null);
  const [showEswatiniArrivalModal, setShowEswatiniArrivalModal] = useState(false);
  const [showBulkSaModal, setShowBulkSaModal] = useState(false);
  const [showBulkSaNotificationModal, setShowBulkSaNotificationModal] = useState(false);
  const [showBulkEswatiniModal, setShowBulkEswatiniModal] = useState(false);

  useEffect(() => {
    setOrderItems(initialOrderItems);
  }, [initialOrderItems]);

  const canBulkMarkSa = orderItems.some((item) => item.can_update_sa_arrival_date);
  const canBulkMarkEswatini = orderItems.length > 0 && orderItems.every((item) => !!item.arrived_at_sa_warehouse) && orderItems.some((item) => item.can_update_eswatini_arrival_date);

  const handleMarkAsArrivedInSA = (itemId: number) => {
    setPendingItemId(itemId);
    setShowArrivalModal(true);
  };

  const handleArrivalConfirm = () => {
    setShowArrivalModal(false);
    setShowNotificationModal(true);
  };

  const handleArrivalCancel = () => {
    setShowArrivalModal(false);
  };

  const handleNotificationConfirm = async (sendNotification: boolean) => {
    if (pendingItemId === null) return;
    const res = await fetchWithAuth(`/order_items/${pendingItemId}/mark_as_arrived_in_sa`, {
      method: 'POST',
      body: JSON.stringify({ send_notification: sendNotification })
    });
    if (res.ok) {
      const updatedOrderItems = orderItems.map((item) =>
        item.id === pendingItemId ? { ...item, arrived_at_sa_warehouse: new Date().toISOString(), can_update_eswatini_arrival_date: true } : item
      );
      setOrderItems(updatedOrderItems);
      onSaArrival?.();
      toast.success("Order Item successfully marked as arrived in SA Warehouse.");
    } else {
      toast.error("Failed to update item.");
    }
    setShowNotificationModal(false);
    setPendingItemId(null);
  };

  const handleMarkAsArrivedInEswatini = (itemId: number) => {
    setPendingItemId(itemId);
    setShowEswatiniArrivalModal(true);
  };

  const handleEswatiniConfirm = async () => {
    setShowEswatiniArrivalModal(false);
    if (pendingItemId === null) return;
    const res = await fetchWithAuth(`/order_items/${pendingItemId}/mark_as_arrived_in_eswatini`, {
      method: 'POST'
    });
    if (res.ok) {
      const updatedOrderItems = orderItems.map((item) =>
        item.id === pendingItemId ? { ...item, arrived_in_eswatini: new Date().toISOString() } : item
      );
      setOrderItems(updatedOrderItems);
      onEswatiniArrival?.();
      toast.success("Order Item successfully marked as arrived in Eswatini.");
    } else {
      toast.error("Failed to update item.");
    }
    setPendingItemId(null);
  };

  const handleEswatiniCancel = () => {
    setShowEswatiniArrivalModal(false);
  };

  const handleBulkSaNotificationConfirm = async (sendNotification: boolean) => {
    setShowBulkSaNotificationModal(false);
    const res = await fetchWithAuth(`/order_items/bulk_mark_as_arrived_in_sa`, {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId, send_notification: sendNotification })
    });
    if (res.ok) {
      const updatedItems: OrderItem[] = await res.json();
      const updatedById = Object.fromEntries(updatedItems.map((i) => [i.id, i]));
      setOrderItems((prev) =>
        prev.map((item) => (updatedById[item.id] ? { ...item, ...updatedById[item.id] } : item))
      );
      onSaArrival?.();
      toast.success("All eligible items marked as arrived in SA Warehouse.");
    } else {
      toast.error("Failed to bulk update items.");
    }
  };

  const handleBulkEswatiniConfirm = async () => {
    setShowBulkEswatiniModal(false);
    const res = await fetchWithAuth(`/order_items/bulk_mark_as_arrived_in_eswatini`, {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId })
    });
    if (res.ok) {
      const updatedItems: OrderItem[] = await res.json();
      const updatedById = Object.fromEntries(updatedItems.map((i) => [i.id, i]));
      setOrderItems((prev) =>
        prev.map((item) => (updatedById[item.id] ? { ...item, ...updatedById[item.id] } : item))
      );
      onEswatiniArrival?.();
      toast.success("All eligible items marked as arrived in Eswatini.");
    } else {
      toast.error("Failed to bulk update items.");
    }
  };

  return (
    <div>
        <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
          <h3 className="text-lg font-semibold whitespace-nowrap">Order Items</h3>
          <div className="flex flex-wrap gap-2 items-center">
            {canBulkMarkSa && (
              <button
                className="text-sm text-blue-600 hover:underline"
                onClick={() => setShowBulkSaModal(true)}
              >
                Mark all as Arrived (SA)
              </button>
            )}
            {canBulkMarkEswatini && (
              <button
                className="text-sm text-blue-600 hover:underline"
                onClick={() => setShowBulkEswatiniModal(true)}
              >
                Mark all as Arrived (Eswatini)
              </button>
            )}
            {onRetryCart && (
              <button
                type="button"
                className="text-sm text-blue-600 hover:underline whitespace-nowrap disabled:opacity-50 disabled:no-underline"
                onClick={onRetryCart}
                disabled={cartRetryState !== 'idle'}
              >
                {cartRetryState === 'sending' ? 'Retrying...' : cartRetryState === 'requested' ? 'Retry Requested' : 'Retry Cart Load'}
              </button>
            )}
            {(onViewCart || cartStoreName) && (
              <span title={!onViewCart ? 'Cart was not extracted' : undefined} className="inline-block">
                <button
                  className={`px-4 py-2 rounded-md font-semibold ${onViewCart ? 'bg-black text-white hover:bg-gray-800' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                  onClick={onViewCart}
                  disabled={!onViewCart}
                >
                  View {cartStoreName ?? 'Store'} Cart
                </button>
              </span>
            )}
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full bg-white border border-gray-200 rounded-md shadow-sm">
            <thead className="bg-gray-50 text-sm text-gray-700 font-semibold">
            <tr className="text-left text-sm font-semibold text-gray-700">
              <th className="px-2 md:px-4 py-2">Store</th>
              <th className="px-2 md:px-4 py-2">Description</th>
              <th className="px-2 md:px-4 py-2">URL</th>
              <th className="px-2 py-2">Qty</th>
              <th className="hidden md:table-cell px-4 py-2">Unit Price</th>
              <th className="px-2 md:px-4 py-2 whitespace-nowrap">Total Price</th>
              <th className="hidden md:table-cell px-4 py-2">Cost</th>
              {showChargeVatColumn && <th className="hidden md:table-cell px-4 py-2 text-center">Charge VAT?</th>}
              <th className="hidden md:table-cell px-4 py-2">Arrived in SA Warehouse</th>
              <th className="hidden md:table-cell px-4 py-2">Arrived in Eswatini</th>
            </tr>
          </thead>
        <tbody>
          {orderItems.length === 0 ? (
            <tr>
              <td colSpan={showChargeVatColumn ? 10 : 9} className="px-4 py-4 text-center text-gray-500">
                No order items found.
              </td>
            </tr>
          ) : (
            orderItems.map((item) => (
              <tr key={item.id} className="border-t border-gray-100 text-sm text-gray-800">
                <td className="px-2 md:px-4 py-2">{item.store.name}</td>
                <td className="px-2 md:px-4 py-2">
                  <div>{item.description}</div>
                  {item.detailed_description && (
                    <div className="text-xs text-gray-500 mt-0.5">{item.detailed_description}</div>
                  )}
                </td>
                <td className="px-2 md:px-4 py-2">
                  {autoExtraction && item.link ? (
                    <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">link</a>
                  ) : (
                    <span className="text-gray-500">N/A</span>
                  )}
                </td>
                <td className="px-2 py-2">{item.quantity}</td>
                <td className="hidden md:table-cell px-4 py-2">{formatCurrency(item.unit_price)}</td>
                <td className="px-2 md:px-4 py-2 whitespace-nowrap">{formatCurrency(item.total_price)}</td>
                <td className="hidden md:table-cell px-4 py-2">{formatCurrency(item.cost)}</td>
                {showChargeVatColumn && (
                  <td className="hidden md:table-cell px-4 py-2 text-center">
                    {item.should_charge_vat ? (
                      <span className="text-green-600">✓</span>
                    ) : (
                      <span className="text-red-600">✗</span>
                    )}
                  </td>
                )}
                <td className="hidden md:table-cell px-4 py-2">
                    {item.arrived_at_sa_warehouse ? (
                    <span className="inline-block w-3 h-3 bg-green-500 rounded-full"></span>
                    ) : item.can_update_sa_arrival_date ? (
                    <button
                      className="text-blue-600 hover:underline"
                      onClick={() => handleMarkAsArrivedInSA(item.id)}
                    >
                      Mark as Arrived
                    </button>
                    ) : (
                    <span className="text-gray-500">Can't update yet</span>
                    )}
                </td>
                <td className="hidden md:table-cell px-4 py-2">
                    {item.arrived_in_eswatini ? (
                    <span className="inline-block w-3 h-3 bg-green-500 rounded-full"></span>
                    ) : item.can_update_eswatini_arrival_date ? (
                    <button
                      className="text-blue-600 hover:underline"
                      onClick={() => handleMarkAsArrivedInEswatini(item.id)}
                    >
                      Mark as Arrived
                    </button>
                    ) : (
                    <span className="text-gray-500">Can't update yet</span>
                    )}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>
      {showArrivalModal && (
        <ConfirmationModal
          isOpen={showArrivalModal}
          message="Are you sure you want to mark this order item as: arrived at the SA Warehouse?"
          onConfirm={handleArrivalConfirm}
          onCancel={handleArrivalCancel}
        />
      )}
      {showNotificationModal && (
        <ConfirmationModal
          isOpen={showNotificationModal}
          message="Would you like to send a 'Arrived in SA Warehouse' WhatsApp notification to the user?"
          onConfirm={() => handleNotificationConfirm(true)}
          onCancel={() => handleNotificationConfirm(false)}
        />
      )}
      {showEswatiniArrivalModal && (
        <ConfirmationModal
          isOpen={showEswatiniArrivalModal}
          message="Are you sure you want to mark this order item as: Arrived in Eswatini?"
          onConfirm={handleEswatiniConfirm}
          onCancel={handleEswatiniCancel}
        />
      )}
      {showBulkSaModal && (
        <ConfirmationModal
          isOpen={showBulkSaModal}
          message="Are you sure you want to mark all eligible items as arrived at the SA Warehouse?"
          onConfirm={() => { setShowBulkSaModal(false); setShowBulkSaNotificationModal(true); }}
          onCancel={() => setShowBulkSaModal(false)}
        />
      )}
      {showBulkSaNotificationModal && (
        <ConfirmationModal
          isOpen={showBulkSaNotificationModal}
          message="Would you like to send 'Arrived in SA Warehouse' WhatsApp notifications to the customer?"
          onConfirm={() => handleBulkSaNotificationConfirm(true)}
          onCancel={() => handleBulkSaNotificationConfirm(false)}
        />
      )}
      {showBulkEswatiniModal && (
        <ConfirmationModal
          isOpen={showBulkEswatiniModal}
          message="Are you sure you want to mark all eligible items as arrived in Eswatini?"
          onConfirm={handleBulkEswatiniConfirm}
          onCancel={() => setShowBulkEswatiniModal(false)}
        />
      )}
    </div>
  );
};

export default OrderItemsTable;
